/**
 * ColorCustomizer.jsx - lets a user recolour project bars and remove custom
 * projects. Choices are shared - everyone looking at the calendar sees them.
 */
import { useEffect, useRef } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { createPortal } from 'react-dom';
import { Icon } from '../ui/Icon';
import { defaultProjectColor } from './manpowerHelpers';

/**
 * Floating panel listing every project label in play, with its colour swatch
 * and a native colour input. Changes go straight into the shared store and
 * show up on the calendar as you make them.
 */
export function ColorCustomizer({ projects, customProjects, palette, setPaletteOverride, onDeleteCustomProject, onClose, focusCode }) {
  const ref = useRef(null);
  const drag = useDraggableDialog(true, { ref }); // moved by its header (10/9/26)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(13,13,13,0.45)',
      }}
    >
      <div
        ref={ref}
        onPointerDown={drag.onPointerDown}
        className="go-pop-in"
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 480,
          maxHeight: '80vh',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...drag.style,
        }}
      >
        <div
          data-dialog-handle=""
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid #E5E9DD',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.6, textTransform: 'uppercase' }}>Customize</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0D0D0D', marginTop: 2 }}>Project colors</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>
              Pick a new color, or delete a user-added project. Changes sync across all browsers.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#fff',
              border: '1px solid #E5E9DD',
              color: '#444',
              width: 32,
              height: 32,
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="x" size={13} color="#444" />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '8px 0' }}>
          {projects.map((code) => {
            const current = palette[code] || defaultProjectColor(code);
            const isOverride = !!palette[code];
            const isFocus = code === focusCode;
            const isCustom = (customProjects || []).includes(code);
            return (
              <div
                key={code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 18px',
                  background: isFocus ? '#F1F8E9' : 'transparent',
                }}
              >
                <div
                  style={{ width: 30, height: 30, borderRadius: 6, background: current, border: '1px solid #00000022', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: '#0D0D0D',
                      fontFamily: 'JetBrains Mono, monospace',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {code}
                  </div>
                  <div style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>
                    {isCustom ? 'User-added' : isOverride ? 'Custom color' : 'Default color'}
                  </div>
                </div>
                <input
                  type="color"
                  value={current}
                  onChange={(e) => setPaletteOverride(code, e.target.value)}
                  style={{
                    width: 36,
                    height: 30,
                    padding: 0,
                    border: '1px solid #E5E9DD',
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: 'transparent',
                  }}
                />
                {isOverride && (
                  <button
                    onClick={() => setPaletteOverride(code, null)}
                    title="Reset color to default"
                    style={{
                      border: '1px solid #E5E9DD',
                      background: '#fff',
                      color: '#666',
                      padding: '5px 10px',
                      borderRadius: 6,
                      fontSize: 10.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Reset
                  </button>
                )}
                {isCustom && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete project "${code}"?\n\nThis also clears every assignment that uses it.`)) {
                        onDeleteCustomProject(code);
                      }
                    }}
                    title="Delete this user-added project and clear every assignment using it"
                    style={{
                      border: '1px solid #FFCDD2',
                      background: '#fff',
                      color: '#B71C1C',
                      padding: '5px 10px',
                      borderRadius: 6,
                      fontSize: 10.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Icon name="trash" size={11} color="#B71C1C" /> Delete
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '10px 18px', borderTop: '1px solid #E5E9DD', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={() => {
              projects.forEach((c) => setPaletteOverride(c, null));
            }}
            style={{
              border: '1px solid #E5E9DD',
              background: '#fff',
              color: '#666',
              padding: '7px 14px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Reset all
          </button>
          <button
            onClick={onClose}
            style={{
              border: '1px solid #0F3D14',
              background: '#1B5E20',
              color: '#fff',
              padding: '7px 14px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
